import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { simpleIAPService } from '../../services/iap-simple.service';
// Note: RevenueCat types not imported directly - using 'any' for web compatibility
// The actual types are still enforced at runtime on native platforms
import { AppThunk } from '..';
import { updateData, getData } from '../../services/firebase/api-wrapper.service';
import logger from '../../utils/logger';

// Type aliases for RevenueCat types (use 'any' for web compatibility)
type PurchasesPackage = any;
type CustomerInfo = any;

const premiumLogger = logger.module('Premium');

interface PremiumState {
  // Core state - matches original app
  isPeakSub: boolean;
  isAdFree: boolean;
  availableOfferings: PurchasesPackage[];
  activeSubIDs: string[];

  // Admin-granted Peak (Firebase fallback)
  adminGrantedPeak: boolean;

  // Loading states
  isLoading: boolean;
  purchaseStatus: 'idle' | 'loading' | 'success' | 'error';
  restoreStatus: 'idle' | 'loading' | 'success' | 'error';
  error: string | null;
}

const initialState: PremiumState = {
  isPeakSub: false,
  isAdFree: false,
  availableOfferings: [],
  activeSubIDs: [],
  adminGrantedPeak: false,
  isLoading: false,
  purchaseStatus: 'idle',
  restoreStatus: 'idle',
  error: null,
};

// Simple async actions matching original app

/**
 * Fetch available offerings from RevenueCat
 */
export const fetchOfferings = createAsyncThunk(
  'premium/fetchOfferings',
  async () => {
    const offerings = await simpleIAPService.getOfferings();
    return offerings;
  }
);

/**
 * Purchase a package
 */
export const purchasePackage = createAsyncThunk(
  'premium/purchasePackage',
  async (packageToPurchase: PurchasesPackage) => {
    premiumLogger.info('Starting purchase for package:', packageToPurchase.identifier);

    try {
      const result = await simpleIAPService.purchasePackage(packageToPurchase);

      if (!result.success) {
        // Check for specific user cancellation
        if (result.error?.userCancelled) {
          premiumLogger.info('User cancelled purchase');
          // Don't throw on cancellation, just return null
          return null;
        }
        premiumLogger.error('Purchase failed:', result.error);
        throw result.error || new Error('Purchase failed');
      }

      premiumLogger.info('Purchase successful, fetching customer info');

      // Return the customer info from the result if available
      // Otherwise fetch it
      const customerInfo = result.customerInfo || await simpleIAPService.getCustomerInfo();

      if (customerInfo) {
        premiumLogger.info('Customer info after purchase:', {
          isPeak: customerInfo.entitlements.active['peak']?.isActive,
          activeProducts: Object.keys(customerInfo.allPurchasedProductIdentifiers || {})
        });
      } else {
        premiumLogger.warn('No customer info available after purchase');
      }

      return customerInfo;
    } catch (error: any) {
      premiumLogger.error('Purchase thunk error:', error);
      // Ensure we properly reject with an error object
      throw error;
    }
  }
);

/**
 * Restore purchases - also refreshes Firebase fallback
 */
export const restorePurchases = createAsyncThunk(
  'premium/restorePurchases',
  async (_, { getState }) => {
    const result = await simpleIAPService.restorePurchases();
    if (!result.success) {
      throw result.error;
    }
    // Get updated customer info after restore
    const customerInfo = await simpleIAPService.getCustomerInfo();

    // Also re-check Firebase for admin-granted Peak (in case it was updated)
    const state = getState() as any;
    const currentUser = state.auth?.user;
    let adminGrantedPeak = false;

    if (currentUser?.uid) {
      try {
        const userData = await getData(`users/${currentUser.uid}`);
        adminGrantedPeak = userData?.adminGrantedPeak === true || userData?.isPeakSub === true;
        premiumLogger.info('Firebase Peak check during restore:', {
          adminGrantedPeak: userData?.adminGrantedPeak,
          isPeakSub: userData?.isPeakSub,
          resolved: adminGrantedPeak
        });
      } catch (error) {
        premiumLogger.error('Failed to check Firebase Peak status during restore:', error);
      }
    }

    return { customerInfo, adminGrantedPeak };
  }
);

/**
 * Check current subscription status (RevenueCat + Firebase adminGrantedPeak fallback)
 */
export const checkSubscriptionStatus = createAsyncThunk(
  'premium/checkStatus',
  async (_, { getState, dispatch }) => {
    const state = getState() as any;
    const currentUser = state.auth?.user; // Fixed: user is stored in auth.user, not user.currentUser

    // Check Firebase for admin-granted Peak first (fallback for admin override)
    let adminGrantedPeak = false;
    if (currentUser?.uid) {
      try {
        const userData = await getData(`users/${currentUser.uid}`);
        adminGrantedPeak = userData?.adminGrantedPeak === true || userData?.isPeakSub === true;
        premiumLogger.info('Firebase Peak check:', {
          adminGrantedPeak: userData?.adminGrantedPeak,
          isPeakSub: userData?.isPeakSub,
          resolved: adminGrantedPeak
        });
      } catch (error) {
        premiumLogger.error('Failed to check Firebase Peak status:', error);
      }
    }

    // Check RevenueCat
    const customerInfo = await simpleIAPService.getCustomerInfo();

    // Sync RevenueCat status to Firebase (but don't overwrite admin-granted)
    if (customerInfo) {
      const rcPeakSub = customerInfo.entitlements.active['peak']?.isActive || false;

      if (currentUser?.uid && !adminGrantedPeak) {
        try {
          await updateData(`users/${currentUser.uid}`, { isPeakSub: rcPeakSub });
          premiumLogger.info('✅ Synced isPeakSub to Firebase from status check:', rcPeakSub);
        } catch (error) {
          premiumLogger.error('Failed to sync isPeakSub to Firebase:', error);
        }
      }
    }

    // Return both RevenueCat info and Firebase override status
    return { customerInfo, adminGrantedPeak };
  }
);

const premiumSliceSimple = createSlice({
  name: 'premium',
  initialState,
  reducers: {
    // Update subscription status from customer info
    updateSubscriptionStatus: (state, action: PayloadAction<CustomerInfo | null>) => {
      const customerInfo = action.payload;

      if (!customerInfo) {
        state.isPeakSub = false;
        state.isAdFree = false;
        state.activeSubIDs = [];
        return;
      }

      // Check for peak entitlement (matches original app)
      const peakEntitlement = customerInfo.entitlements.active['peak'];
      state.isPeakSub = peakEntitlement?.isActive || false;

      premiumLogger.info('Premium status check:', {
        allEntitlements: Object.keys(customerInfo.entitlements?.active || {}),
        peakEntitlement: peakEntitlement,
        isPeakSub: state.isPeakSub,
        allProducts: customerInfo.allPurchasedProductIdentifiers,
        rawEntitlements: customerInfo.entitlements,
        activeEntitlementsDetailed: Object.entries(customerInfo.entitlements?.active || {}).map(([key, value]: [string, any]) => ({
          key,
          isActive: value?.isActive,
          productIdentifier: value?.productIdentifier
        }))
      });

      // Check for ad-free entitlement
      const adFreeEntitlement = customerInfo.entitlements.active['ad-free'];
      state.isAdFree = adFreeEntitlement?.isActive || false;

      // Track active subscription IDs
      const activeIDs: string[] = [];
      if (peakEntitlement?.productIdentifier) {
        activeIDs.push(peakEntitlement.productIdentifier);
      }
      if (adFreeEntitlement?.productIdentifier) {
        activeIDs.push(adFreeEntitlement.productIdentifier);
      }
      state.activeSubIDs = activeIDs;
    },

    clearError: (state) => {
      state.error = null;
    },

    clearPurchaseStatus: (state) => {
      state.purchaseStatus = 'idle';
    },

    clearRestoreStatus: (state) => {
      state.restoreStatus = 'idle';
    },

    // Set admin-granted Peak from Firebase
    setAdminGrantedPeak: (state, action: PayloadAction<boolean>) => {
      state.adminGrantedPeak = action.payload;
      premiumLogger.info('Admin-granted Peak set to:', action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch offerings
    builder
      .addCase(fetchOfferings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOfferings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.availableOfferings = action.payload;
      })
      .addCase(fetchOfferings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch offerings';
      });

    // Purchase package
    builder
      .addCase(purchasePackage.pending, (state) => {
        state.purchaseStatus = 'loading';
        state.error = null;
      })
      .addCase(purchasePackage.fulfilled, (state, action) => {
        premiumLogger.info('Purchase fulfilled, updating state');

        // Handle user cancellation
        if (action.payload === null) {
          premiumLogger.info('Purchase cancelled by user');
          state.purchaseStatus = 'idle';
          return;
        }

        state.purchaseStatus = 'success';
        // Update status from customer info
        if (action.payload) {
          premiumSliceSimple.caseReducers.updateSubscriptionStatus(state, {
            payload: action.payload,
            type: 'updateSubscriptionStatus'
          });
        }
      })
      .addCase(purchasePackage.rejected, (state, action) => {
        premiumLogger.error('Purchase rejected:', action.error);
        state.purchaseStatus = 'error';
        state.error = action.error.message || 'Purchase failed';
      });

    // Restore purchases
    builder
      .addCase(restorePurchases.pending, (state) => {
        state.restoreStatus = 'loading';
        state.error = null;
      })
      .addCase(restorePurchases.fulfilled, (state, action) => {
        state.restoreStatus = 'success';
        const { customerInfo, adminGrantedPeak } = action.payload;

        // Update Firebase admin-granted Peak status
        state.adminGrantedPeak = adminGrantedPeak;

        // Update status from RevenueCat customer info
        premiumSliceSimple.caseReducers.updateSubscriptionStatus(state, {
          payload: customerInfo,
          type: 'updateSubscriptionStatus'
        });

        premiumLogger.info('Restore complete:', {
          rcPeak: state.isPeakSub,
          adminGrantedPeak: state.adminGrantedPeak,
          effectivePeak: state.isPeakSub || state.adminGrantedPeak
        });
      })
      .addCase(restorePurchases.rejected, (state, action) => {
        state.restoreStatus = 'error';
        state.error = action.error.message || 'Restore failed';
      });

    // Check subscription status
    builder
      .addCase(checkSubscriptionStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkSubscriptionStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const { customerInfo, adminGrantedPeak } = action.payload;

        // Set admin-granted Peak from Firebase
        state.adminGrantedPeak = adminGrantedPeak;

        // Update status from RevenueCat customer info
        premiumSliceSimple.caseReducers.updateSubscriptionStatus(state, {
          payload: customerInfo,
          type: 'updateSubscriptionStatus'
        });

        premiumLogger.info('Subscription status check complete:', {
          rcPeak: state.isPeakSub,
          adminGrantedPeak: state.adminGrantedPeak,
          effectivePeak: state.isPeakSub || state.adminGrantedPeak
        });
      })
      .addCase(checkSubscriptionStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to check status';
      });
  },
});

// Actions
export const {
  updateSubscriptionStatus,
  clearError,
  clearPurchaseStatus,
  clearRestoreStatus,
  setAdminGrantedPeak
} = premiumSliceSimple.actions;

/**
 * Attach purchase update listener (matches original app pattern)
 */
export const attachPurchaseListener = (): AppThunk => (dispatch: any, getState: any) => {
  premiumLogger.info('Attaching purchase update listener');

  // Listen for customer info updates
  simpleIAPService.addCustomerInfoUpdateListener(async (customerInfo) => {
    // Check for peak entitlement - matches the updateSubscriptionStatus logic
    const isPeakSub = customerInfo.entitlements.active['peak']?.isActive || false;

    premiumLogger.info('Customer info updated via listener:', {
      isPeak: customerInfo.entitlements.active['peak']?.isActive,
      activeProducts: Object.keys(customerInfo.allPurchasedProductIdentifiers || {}),
      isPeakSub
    });

    dispatch(updateSubscriptionStatus(customerInfo));

    // Sync to Firebase
    const state = getState();
    const currentUser = state.auth?.user; // Fixed: user is stored in auth.user, not user.currentUser
    if (currentUser?.uid) {
      try {
        await updateData(`users/${currentUser.uid}`, { isPeakSub });
        premiumLogger.info('✅ Synced isPeakSub to Firebase:', isPeakSub);
      } catch (error) {
        premiumLogger.error('Failed to sync isPeakSub to Firebase:', error);
      }
    }
  });

  // Also do an initial check
  dispatch(checkSubscriptionStatus());
};

// Selectors
export const selectPremiumState = (state: { premium: PremiumState }) => state.premium;
// Returns true if user has Peak via RevenueCat OR admin-granted (Firebase fallback)
export const selectIsPeakSub = (state: { premium: PremiumState }) =>
  state.premium.isPeakSub || state.premium.adminGrantedPeak;
export const selectIsAdFree = (state: { premium: PremiumState }) =>
  state.premium.isAdFree || state.premium.adminGrantedPeak; // Admin Peak includes ad-free
export const selectAdminGrantedPeak = (state: { premium: PremiumState }) => state.premium.adminGrantedPeak;
export const selectPremiumIsLoading = (state: { premium: PremiumState }) => state.premium.isLoading;
export const selectAvailableOfferings = (state: { premium: PremiumState }) => state.premium.availableOfferings;
export const selectActiveSubIDs = (state: { premium: PremiumState }) => state.premium.activeSubIDs;
export const selectPurchaseStatus = (state: { premium: PremiumState }) => state.premium.purchaseStatus;
export const selectRestoreStatus = (state: { premium: PremiumState }) => state.premium.restoreStatus;

// Get current offering (first package group)
export const selectCurrentOffering = (state: { premium: PremiumState }) => {
  const offerings = state.premium.availableOfferings;
  if (offerings.length === 0) return null;

  // Group packages into an offering-like structure
  return {
    identifier: 'default',
    packages: offerings,
    availablePackages: offerings
  };
};

// Export reducer
export default premiumSliceSimple.reducer;